import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { AxiosResponse } from "axios";
import RootState from "store/state";

type RequestError = undefined | {
    data: { 
        error?: string
        type?: string 
        details?: {
            missingFields?: string
            format?: string
        }
    },
    status: number
}


export default function useSmartAgentAPI<T, RS=RootState  , ReturnT=AxiosResponse<T>>(
    fn: (state: RS, ...args: any) => Promise<ReturnT>,
    initialRequest: boolean = true,
    callback: (data?: ReturnT) => void = () => { }
) {
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<RequestError | undefined>(undefined);
    const [response, setResponse] = useState<ReturnT | undefined>(undefined)

    const state = useSelector<RS, RS>(
        state => state
    );

    const getData = useCallback(
        async (...args) => {
            setLoading(true);

            try {
                const response = await fn(state, ...args);
                setResponse(response)
                setError(undefined)
                callback(response)
            } catch (err) {
                setError(err.response);
                setResponse(undefined);
            } finally {
                setLoading(false);
                callback(response);
            }
        },
        // eslint-disable-next-line
        [fn],
    )

    useEffect(() => {
        if (initialRequest) getData()
        // eslint-disable-next-line
    }, [])

    const clear = ()=>{
        setResponse(undefined)
        setError(undefined)
        setLoading(false)
    }

    return [
        error,
        loading,
        response,
        getData,
        clear
    ] as [RequestError, boolean, ReturnT | undefined, (...args: any) => void, ()=>void]
}
